/*
  IA Product Lock — <ia-product-lock> (snippets/ia-product-lock.liquid).
  VIP early-access gate for PDPs tagged "lock-product" and gated-template
  collections. The snippet's inline script stamps `ia-locked` on <html> from
  the cookie before the page paints; this element owns the password form, the
  unlock cookie and the success screen. Content reveals on the next navigation
  (cookie present -> no class); the success screen's button redirects onward,
  or reveals in place when no redirect is set. On the "Shop now" hub
  collection the element also swaps the page title once the deadline passes —
  for unlocked visitors too, so it stays alive (display:none) instead of
  removing itself.

  data-password       expected code, lowercase
  data-passthrough    code as pre-filled by a `?passthrough` marketing link
  data-deadline       "YYYY-MM-DD HH:MM" early-access end (Eastern), optional
  data-redirect       success-button target URL, optional
  data-expired-title  post-deadline page title on the hub collection, optional
*/
import IaCountdown from 'ia-countdown'

const COOKIE_NAME = 'ia_lock_cleared'
const COOKIE_DAYS = 2
const TIMEZONE = 'America/New_York'

if (!customElements.get('ia-product-lock')) {
  class IaProductLock extends HTMLElement {
    connectedCallback() {
      this.deadline = IaCountdown.toEpoch(this.dataset.deadline, TIMEZONE)
      this.locked = document.documentElement.classList.contains('ia-locked')
      this.titleEl = this.dataset.expiredTitle
        ? document.querySelector('.ia-page-title-header__title')
        : null

      if (!this.locked && !this.titleEl) {
        this.remove()
        return
      }

      if (this.locked) {
        this.input = this.querySelector('#ia-lock-code')
        this.error = this.querySelector('[data-lock-error]')

        this.querySelector('[data-lock-form]').addEventListener('submit', this.handleSubmit.bind(this))
        this.querySelector('[data-lock-shop]').addEventListener('click', this.handleShop.bind(this))
        this.input.addEventListener('input', () => {
          this.error.hidden = true
        })

        if (new URLSearchParams(window.location.search).has('passthrough')) {
          this.input.value = this.dataset.passthrough
          this.input.classList.add('is-prefilled')
        }
      }

      this.watchExpiry()
    }

    disconnectedCallback() {
      clearTimeout(this.expiryTimer)
    }

    handleSubmit(event) {
      event.preventDefault()
      const word = this.input.value.trim().toLowerCase()
      if (word && word === this.dataset.password) {
        this.unlock()
      } else {
        this.error.hidden = false
      }
    }

    unlock() {
      const expires = new Date(Date.now() + COOKIE_DAYS * 24 * 60 * 60 * 1000)
      document.cookie = `${COOKIE_NAME}=true;expires=${expires.toUTCString()};path=/`

      this.querySelector('[data-lock-card]').hidden = true
      this.querySelector('[data-lock-success]').hidden = false
      window.scrollTo({ top: 0 })
    }

    handleShop() {
      if (this.dataset.redirect) {
        window.location.replace(this.dataset.redirect)
      } else {
        document.documentElement.classList.remove('ia-locked')
        this.remove()
      }
    }

    /* Swap the success heading / hub title and drop the shop button the moment
       the deadline passes (setTimeout caps at ~24.8 days -> chunked). */
    watchExpiry() {
      if (!this.deadline) return
      const remaining = this.deadline - Date.now()
      if (remaining <= 0) {
        this.applyExpiry()
        return
      }
      this.expiryTimer = setTimeout(() => this.watchExpiry(), Math.min(remaining, 2147483647))
    }

    applyExpiry() {
      const expired = this.querySelector('[data-lock-success-expired]')
      if (expired) {
        this.querySelector('[data-lock-success-title]').hidden = true
        expired.hidden = false
      }
      this.querySelector('[data-lock-shop]').hidden = true
      if (this.titleEl) this.titleEl.textContent = this.dataset.expiredTitle
    }
  }

  customElements.define('ia-product-lock', IaProductLock)
}
